import classNames from "classnames";
import { debounce } from "lodash";
import {
  useEffect,
  useState,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import AutoSuggest from "react-autosuggest";
import { useDispatch, useSelector } from "react-redux";

import { MacroCode, Specimen } from "pwapi";
import { ApiService } from "pwapi";

import { CaseDiagnosis } from "custom-models";

import { setInputMacroCode } from "shared/actions/specimenDetailActions";
import { FeatureFlags } from "shared/reducers/sysReducer";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import useSiteSpecificCodes from "hooks/useSiteSpecificCodes";
import ApplicationState from "store/ApplicationState";
import { searchTermsOverSpecimenSite } from "util/icd10Utils";

import {
  ShadowDiagnosis,
  SiteSpecificCodeReplacement,
} from "../DiagnosisDialog";

interface MacroCodeInputProps {
  setShadowDiagnosis: Function;
  currentSpecimen?: Specimen;
  currentDiagnosis?: CaseDiagnosis;
  setDialog?: Function;
  executeMacroCode: Function;
  initialValue?: string;
  prospectiveAI?: boolean;
  useHelpers?: boolean;
  clearDiagnosis: Function;
  setSiteSpecificCode?: React.Dispatch<
    React.SetStateAction<SiteSpecificCodeReplacement | undefined>
  >;
}

interface Suggestion {
  title: string;
  description: string;
}

const macroCodeRegex =
  /^(?<frontCode>(?<front>[a-zA-Z]+):){0,1}(?<dxcode>[a-zA-Z0-9/]{1,}){0,1}(?<marginCode>\.(?<margin>\w+)){0,1}(?<commentCode>;(?<comment>\w+)){0,1}.*$/;
export const extractHelpers = (macroCode: string, useHelpers: boolean = true) =>
  useHelpers
    ? macroCode.match(macroCodeRegex)?.groups || {}
    : {
        front: undefined,
        dxcode: macroCode,
        margin: undefined,
        comment: undefined,
      };

export const mapHelpersToSuggestions = (helpers: { [key: string]: string }) => {
  return Object.entries(helpers).map(([title, description]) => ({
    title,
    description,
  }));
};

export const reconstructMacroCode = ({
  front,
  dxcode,
  margin,
  comment,
}: {
  front?: string;
  dxcode?: string;
  margin?: string;
  comment?: string;
}) => {
  return `${front ? `${front}:` : ""}${dxcode ?? ""}${
    margin ? `.${margin}` : ""
  }${comment ? `;${comment}` : ""}`;
};

export const buildCombinedHelper = (
  characters: string | undefined,
  codes: { [key: string]: string }
) => {
  return characters
    ?.split("")
    .map((c) => codes[c])
    .join(" ");
};

export const mergeNotes = (...args: (string | undefined | null)[]) => {
  return args.filter((a) => a).join(" ");
};

// Which "helper code" we are in (section name) under the cursor
// And the "value" left of the cursor (helpful for autosuggest)
export const macroCodeSection = (
  pos: number,
  macroCode: string,
  useHelpers: boolean = true
): {
  name: string;
  value: string;
} => {
  if (!useHelpers) {
    return { name: "dxcode", value: macroCode };
  }
  if (pos) {
    const newStr = macroCode.substr(0, pos) || "";
    const { front, dxcode, margin, comment } = extractHelpers(newStr);
    const colonIndex = macroCode.indexOf(":");
    const leftOfColon =
      colonIndex > -1 ? pos < macroCode.indexOf(":") + 1 : false;

    if (dxcode && newStr.includes(";")) {
      return { name: "comment", value: comment || "" };
    } else if (dxcode && newStr.includes(".")) {
      return { name: "margin", value: margin || "" };

      // Two special cases for multiple front-helpers:
    } else if (leftOfColon) {
      // If we are left of the Colon of a fuller code, assume front
      return { name: "front", value: front || "" };
      // If we have just started typing, assume multi-characters
      // at the beginning is actually a dxcode
    } else if (newStr.length > 1 && !newStr.includes(":")) {
      return { name: "dxcode", value: dxcode };

      // Back to the "normal" cases
    } else if (newStr.includes(":")) {
      return { name: "dxcode", value: dxcode };
    } else {
      return { name: "front", value: front || dxcode };
    }
  }
  return { name: "front", value: "" };
};

const MacroCodeInput = forwardRef(
  (
    {
      setShadowDiagnosis,
      currentSpecimen,
      currentDiagnosis,
      setDialog,
      executeMacroCode,
      initialValue,
      prospectiveAI,
      setSiteSpecificCode,
      useHelpers = true,
      clearDiagnosis,
    }: MacroCodeInputProps,
    fwdRef
  ) => {
    const dispatch = useDispatch();
    const macroInput = useRef<HTMLInputElement>(null);
    // Allow parent to also have a ref to the input
    // Parent will only use this to .focus() the input
    useImperativeHandle(fwdRef, () => ({
      focus: () => {
        if (macroInput.current) {
          macroInput.current.focus();
        }
      },
    }));

    const features: FeatureFlags = useSelector(({ sys }) => sys.features);

    const value = useSelector(
      (state: ApplicationState) =>
        state.caseDetail.specimenDetail.find(
          (s) => s.id === currentSpecimen?.id
        )?.inputMacroCode
    );
    const macroCode = value || "";

    const setMacroCode = (newValue: string | Function) => {
      if (typeof newValue === "function") {
        newValue = newValue(macroCode);
      }
      currentSpecimen?.id &&
        dispatch(setInputMacroCode(currentSpecimen.id, newValue as string));
    };
    const handleClearCode = () => {
      setMacroCode("");
      clearDiagnosis();
    };

    const lastExecutedValue = reconstructMacroCode(
      currentDiagnosis?.macro_code || {}
    );

    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    const [diagnosisFromServer, setDiagnosisFromServer] = useState<MacroCode>();

    const helperCodes = useSelector(
      (state: ApplicationState) => state.macroHelper
    );

    const { front, dxcode, margin, comment } = extractHelpers(
      macroCode,
      useHelpers
    );

    const fetchDiagnosisCodes = async (dxcode: string) => {
      return ApiService.listMacroCodes({ dxcode });
    };

    // Choose which suggestions to load
    const fetchSuggestions = useCallback(
      debounce(
        async ({
          value,
          reason,
        }: AutoSuggest.SuggestionsFetchRequestedParams) => {
          // To avoid churning up and down suggestion lists
          // such that navigating the suggestion doesn't re-fire and thus
          // reshuffle the actual suggestion elements, don't change suggestions
          if (
            !(
              reason === "input-changed" ||
              (reason === "input-focused" && value.length === 0)
            )
          )
            return;

          const section = macroCodeSection(
            macroInput.current?.selectionStart || 0,
            value,
            useHelpers
          );

          switch (section.name) {
            case "comment":
              setSuggestions(mapHelpersToSuggestions(helperCodes.comment));
              break;
            case "margin":
              setSuggestions(mapHelpersToSuggestions(helperCodes.margin));
              break;
            case "dxcode":
              const codes = await fetchDiagnosisCodes(
                section.value?.toUpperCase()
              );
              setSuggestions(
                codes.results?.map((r) => ({
                  title: r.dxcode,
                  description: r.diagnosis,
                })) || []
              );
              break;
            default:
              setSuggestions(mapHelpersToSuggestions(helperCodes.front));
          }
        },
        200,
        { trailing: true }
      ),
      [helperCodes]
    );

    const setPartialMacroCode = (newValue: string) => {
      setMacroCode((macroCode: string) => {
        let { front, dxcode, margin, comment } = extractHelpers(
          macroCode,
          useHelpers
        );
        const section = macroCodeSection(
          macroInput.current?.selectionStart || 0,
          macroCode,
          useHelpers
        );

        // Now that we support multiple front- and comment- codes
        // Some are replacement; some are additive
        switch (section.name) {
          case "front":
            // Additive
            front = (front || "") + newValue;
            break;
          case "dxcode":
            // Replace
            dxcode = newValue;
            break;
          case "margin":
            // Replace
            margin = newValue;
            break;
          case "comment":
            // Additive
            comment = comment + newValue;
            break;
        }

        if (
          macroCode &&
          (macroCode.length < 2 || (dxcode && dxcode.length < 2))
        ) {
          dxcode = "";
        }

        return reconstructMacroCode({ front, dxcode, margin, comment });
      });
    };

    // TODO: to actually execute this, we have to fetch a DXCODE
    // An improvement might be to cache these, but for now we'll re-fetch
    const fetchDXCode = useCallback(
      debounce(
        async (dxcode: string) => {
          if (dxcode) {
            const d = await ApiService.listMacroCodes({
              dxcodeIexact: dxcode,
              limit: 1,
            });
            setDiagnosisFromServer(d.results && d.results[0]);
          } else {
            setDiagnosisFromServer(undefined);
          }
        },
        200,
        { trailing: true }
      ),
      []
    );

    useEffect(() => {
      fetchDXCode(dxcode);
    }, [dxcode]);

    // TODO: are cases being accurately shuttled around?
    useEffect(() => {
      setShadowDiagnosis((_s: ShadowDiagnosis) => ({
        ...diagnosisFromServer,

        front: buildCombinedHelper(front?.toLowerCase(), helperCodes.front),
        margin: helperCodes.margin[margin?.toLowerCase() || ""],
        note: mergeNotes(
          diagnosisFromServer?.note,
          buildCombinedHelper(comment?.toLowerCase(), helperCodes.comment)
        ),
      }));
    }, [front, margin, comment, diagnosisFromServer, helperCodes]);

    useEffect(() => {
      if (typeof value === "undefined" && (lastExecutedValue || initialValue)) {
        setMacroCode(lastExecutedValue || initialValue || "");
      }
    }, [value, lastExecutedValue, initialValue]);

    useEffect(() => {
      if (!macroCode) macroInput.current?.focus();
    }, [currentSpecimen?.id, macroCode]);

    const {
      siteSpecificICD10Feature,
      siteSpecificCodesLoaded,
      nosCodes,
      nosToSiteSpecificCodes,
      termsToSite,
    } = useSiteSpecificCodes({ fetchCodes: true });

    const onExecuteMacroCode = async () => {
      if (!value) return;
      let apiDiagnosis = undefined;
      if (dxcode) {
        const d = await ApiService.listMacroCodes({
          dxcodeIexact: dxcode,
          limit: 1,
        });
        apiDiagnosis = d.results && d.results[0];
      }

      let replacementCode = undefined;
      if (apiDiagnosis) {
        const siteOrSource = currentSpecimen?.site || currentSpecimen?.source;

        // Let's get "ICD10"-specific!
        if (
          siteOrSource &&
          siteSpecificICD10Feature &&
          siteSpecificCodesLoaded &&
          // this is mostly so we don't have to ?. exessively inside
          nosToSiteSpecificCodes &&
          termsToSite &&
          nosCodes?.includes(apiDiagnosis.icd10_code.code)
        ) {
          const potentialReplacement =
            nosToSiteSpecificCodes[apiDiagnosis.icd10_code.code];
          const siteTerm = searchTermsOverSpecimenSite(
            Object.keys(termsToSite),
            siteOrSource
          );
          const location = siteTerm ? termsToSite[siteTerm] : undefined;

          if (location) {
            replacementCode =
              potentialReplacement[
                location as keyof typeof potentialReplacement
              ];
          }

          if (replacementCode) {
            const originalCode = apiDiagnosis.icd10_code;
            apiDiagnosis.icd10_code = {
              code: replacementCode,
              description: "",
            };

            setSiteSpecificCode &&
              setSiteSpecificCode({
                originalCode,
                replacementCode: apiDiagnosis.icd10_code,
              });
          }
        }

        executeMacroCode(macroCode, {
          ...apiDiagnosis,
          front: buildCombinedHelper(front?.toLowerCase(), helperCodes.front),
          margin: helperCodes.margin[margin?.toLowerCase() || ""],
          note: mergeNotes(
            apiDiagnosis?.note,
            buildCombinedHelper(comment?.toLowerCase(), helperCodes.comment)
          ),
        });
      }
    };

    return (
      <div className="Macro">
        <div
          className={classNames("Macro__input", {
            "Macro__input--AI": value === initialValue,
          })}
        >
          <AutoSuggest
            suggestions={suggestions}
            getSuggestionValue={(s) => s?.title}
            inputProps={{
              value: macroCode,
              placeholder: prospectiveAI ? initialValue : "Macro Code",

              onChange: (e, { newValue, method }) => {
                if (method === "type") {
                  setMacroCode(newValue);
                }
                // NOTE: in my own use, I found this annoying
                // Better UX to execute on a selection
                // } else if (method === "up" || method === "down") {
                //   setPartialMacroCode(newValue);
                // }
              },

              spellCheck: false,

              onKeyUp: (e) => {
                if ([37, 39].includes(e.keyCode)) {
                  fetchSuggestions({
                    value: (e.target as HTMLInputElement).value,
                    reason: "input-changed",
                  });
                }
              },
              onKeyDown: (e) => {
                if (e.key === "Escape" && setDialog) {
                  setDialog(null);
                }
              },
              onKeyPress: (e) => {
                if (e.key === "Enter" || e.code === "Enter") {
                  e.preventDefault();
                  onExecuteMacroCode();
                }
              },

              ref: macroInput,

              // @ts-ignore: a custom attribute for lastpass
              "data-lpignore": true,
            }}
            // NOTE: see note above; this proved to be annoying
            // onSuggestionHighlighted={({ suggestion }) =>
            //   suggestion && setPartialMacroCode(suggestion.title)
            // }
            onSuggestionSelected={(event, { suggestion }) =>
              suggestion && setPartialMacroCode(suggestion.title)
            }
            renderSuggestion={(s) => (
              <div>
                <strong>{s.title}</strong>
                <p>{s.description}</p>
              </div>
            )}
            onSuggestionsClearRequested={() => setSuggestions([])}
            onSuggestionsFetchRequested={fetchSuggestions}
            multiSection={false}
          />
          <PathologyWatchButton
            type="button"
            onClick={() => onExecuteMacroCode()}
            disabled={!value}
            noWrap
          >
            <>
              Use{" "}
              {currentDiagnosis?.macro_code &&
                reconstructMacroCode(currentDiagnosis?.macro_code) !==
                  macroCode &&
                "New "}
              Code
            </>
          </PathologyWatchButton>
          {features.canClearDiagnosisFields && (
            <PathologyWatchButton
              type="button"
              onClick={handleClearCode}
              noWrap
              disabled={!currentDiagnosis?.macro_code}
            >
              Clear Code
            </PathologyWatchButton>
          )}
        </div>
      </div>
    );
  }
);

export default MacroCodeInput;
